import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  useTheme
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import { formatMoney } from '../utils/global';

type EmployeePerformanceData = {
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
  totalRevenue: number;
  totalProfit: number;
  totalTransactions: number;
  averageTransactionValue: number;
  profitMargin: number;
  monthlyPerformance: {
    month: string;
    revenue: number;
    profit: number;
    transactions: number;
  }[];
};

type EmployeePerformanceAnalysisProps = {
  data: EmployeePerformanceData[];
  chartType?: 'bar' | 'line';
};

export default function EmployeePerformanceAnalysis({ 
  data, 
  chartType = 'bar' 
}: EmployeePerformanceAnalysisProps) {
  const theme = useTheme();

  const chartData = data.map(item => ({
    name: `${item.firstName} ${item.lastName}`,
    revenue: item.totalRevenue / 100,
    profit: item.totalProfit / 100,
    transactions: item.totalTransactions,
    margin: item.profitMargin
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            background: 'white',
            border: '1px solid #ccc',
            borderRadius: 1,
            p: 2,
            boxShadow: 2
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  borderRadius: '50%'
                }}
              />
              <Typography variant="body2">
                {entry.dataKey === 'revenue' && 'Ciro: '}
                {entry.dataKey === 'profit' && 'Kar: '}
                {entry.dataKey === 'transactions' && 'İşlem Sayısı: '}
                {entry.dataKey === 'margin' && 'Kar Marjı: '}
                {entry.dataKey === 'margin' 
                  ? `${entry.value.toFixed(2)}%`
                  : entry.dataKey === 'transactions'
                  ? `${entry.value} adet`
                  : formatMoney(entry.value.toString())
                }
                {entry.dataKey !== 'margin' && entry.dataKey !== 'transactions' && ' TL'}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  const renderChart = () => {
    if (chartType === 'line') {
      return (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Line 
              type="monotone" 
              dataKey="revenue" 
              stroke="#0088FE" 
              strokeWidth={2}
              name="Ciro"
            />
            <Line 
              type="monotone" 
              dataKey="profit" 
              stroke="#00C49F" 
              strokeWidth={2}
              name="Kar"
            />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="revenue" fill="#0088FE" name="Ciro" />
          <Bar dataKey="profit" fill="#00C49F" name="Kar" />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const getTotalStats = () => {
    const totalRevenue = data.reduce((sum, item) => sum + item.totalRevenue, 0);
    const totalProfit = data.reduce((sum, item) => sum + item.totalProfit, 0);
    const totalTransactions = data.reduce((sum, item) => sum + item.totalTransactions, 0);
    const averageProfitMargin = data.length > 0 
      ? data.reduce((sum, item) => sum + item.profitMargin, 0) / data.length 
      : 0;
    const topPerformer = data.reduce((max, item) => 
      item.totalRevenue > max.totalRevenue ? item : max, data[0] || {});

    return { totalRevenue, totalProfit, totalTransactions, averageProfitMargin, topPerformer };
  };

  const stats = getTotalStats();

  // Sort employees by performance
  const sortedData = [...data].sort((a, b) => b.totalRevenue - a.totalRevenue);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Personel Performans Analizi
      </Typography>

      {/* Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {renderChart()}
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Box sx={{ textAlign: 'center', p: 2, background: '#F8F9FA', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {formatMoney((stats.totalRevenue / 100).toString())} TL
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Toplam Ciro
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Box sx={{ textAlign: 'center', p: 2, background: '#F8F9FA', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
              {formatMoney((stats.totalProfit / 100).toString())} TL
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Toplam Kar
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Box sx={{ textAlign: 'center', p: 2, background: '#F8F9FA', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main' }}>
              {stats.totalTransactions}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Toplam İşlem
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Box sx={{ textAlign: 'center', p: 2, background: '#F8F9FA', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'warning.main' }}>
              {stats.averageProfitMargin.toFixed(2)}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ort. Kar Marjı
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Box sx={{ textAlign: 'center', p: 2, background: '#F8F9FA', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'secondary.main' }}>
              {stats.topPerformer ? `${stats.topPerformer.firstName} ${stats.topPerformer.lastName}` : '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              En Başarılı
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Performance Ranking */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Performans Sıralaması
          </Typography>
          <Grid container spacing={2}>
            {sortedData.slice(0, 3).map((employee, index) => (
              <Grid item xs={12} md={4} key={employee.employeeId}>
                <Box sx={{ 
                  p: 2, 
                  border: '1px solid', 
                  borderColor: index === 0 ? 'gold' : index === 1 ? 'silver' : '#CD7F32',
                  borderRadius: 2,
                  textAlign: 'center',
                  backgroundColor: index === 0 ? '#FFF8DC' : index === 1 ? '#F5F5F5' : '#FDF5E6'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                    {index === 0 && <Typography variant="h4">🥇</Typography>}
                    {index === 1 && <Typography variant="h4">🥈</Typography>}
                    {index === 2 && <Typography variant="h4">🥉</Typography>}
                  </Box>
                  <Avatar sx={{ mx: 'auto', mb: 1 }}>
                    {employee.firstName[0]}{employee.lastName[0]}
                  </Avatar>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {employee.firstName} {employee.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {formatMoney((employee.totalRevenue / 100).toString())} TL
                  </Typography>
                  <Chip
                    label={`${employee.profitMargin.toFixed(2)}% Kar Marjı`}
                    size="small"
                    color={employee.profitMargin > 15 ? 'success' : employee.profitMargin > 10 ? 'warning' : 'error'}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Detailed Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
              <TableCell sx={{ fontWeight: 600 }}>Personel</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Toplam Ciro</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Toplam Kar</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>İşlem Sayısı</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Ort. İşlem</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Kar Marjı</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Sıralama</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((employee, index) => (
              <TableRow key={employee.employeeId} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {employee.firstName[0]}{employee.lastName[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {employee.firstName} {employee.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {employee.email}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formatMoney((employee.totalRevenue / 100).toString())} TL
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                    {formatMoney((employee.totalProfit / 100).toString())} TL
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {employee.totalTransactions}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {formatMoney((employee.averageTransactionValue / 100).toString())} TL
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={`${employee.profitMargin.toFixed(2)}%`}
                    sx={{
                      backgroundColor: employee.profitMargin > 15 ? theme.palette.success.main : 
                                      employee.profitMargin > 10 ? theme.palette.warning.main : 
                                      theme.palette.error.main,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={`#${index + 1}`}
                    color={index === 0 ? 'primary' : index === 1 ? 'secondary' : 'default'}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
